/**
 * 광고 성과 페이지 — 페이지 하나 + 채널 탭(`?channel=`), 기간 프리셋(`?days=`).
 *
 * 정본: `agent_docs/ad_performance_page_design.md` (§0 결정 아론 10-06).
 * - 1차는 **실험실 샵만, 메뉴 없이 경로로만** 연다(사이드바 `visibleFn: () => false`, `/media-mix` 와 같은 방식).
 * - 실제 차단은 서버 게이트(`v2/ad_performance/gate.py`, 실험실 밖·닫힌 채널 = 404)다. 여기 플래그 검사는 노출 정리일 뿐.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'

import { HiddenFeatureNotFound } from '@/components/HiddenFeatureNotFound'
import { Button } from '@/components/ui/Button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { AdPerformanceReport, useAdPerformance } from '@/features/ad-performance/AdPerformanceReport'
import { PERIOD_PRESETS, parsePeriodDays, periodRange } from '@/features/ad-performance/aggregate'
import { CHANNELS, findChannel } from '@/features/ad-performance/channels'
import { useShop } from '@/lib/shopContext'

export const Route = createFileRoute('/_dashboard/ad-performance')({
  component: AdPerformancePage,
  validateSearch: (search: Record<string, unknown>) => ({
    channel: typeof search.channel === 'string' ? search.channel : CHANNELS[0].channel,
    days: parsePeriodDays(search.days),
  }),
})

function AdPerformancePage() {
  // ⭐ 실험실 밖이면 **제목도 띄우지 않고** 잘라낸다 — 쿼리도 안 나간다(HiddenFeatureNotFound 주석).
  //    `ad_channels_section_enabled` 는 실험실 플래그(`shops.lab_features_enabled`)를 그대로 싣는다.
  const { shop } = useShop()
  const { channel, days } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  // 기간은 KST 오늘 기준. 날짜가 바뀌어도 탭을 다시 열기 전엔 같은 구간을 본다(쿼리 키가 흔들리지 않게).
  const range = useMemo(() => periodRange(days), [days])

  const config = findChannel(channel)
  const enabled = !!shop?.ad_channels_section_enabled && !!config
  // 훅은 조건 없이 부른다(규칙) — 꺼져 있으면 `enabled: false` 로 요청이 안 나간다.
  const { summary, ads, notFound } = useAdPerformance(channel, range.date_from, range.date_to, enabled)
  // 플래그가 늦게 반영돼도(샵 응답 캐시 등) 서버가 404 면 같은 화면으로 떨어뜨린다.
  if (!enabled || !config || notFound) return <HiddenFeatureNotFound />

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div>
        <h1 className="text-xl font-bold text-on-surface">광고 성과</h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          연동한 광고 계정에서 매일 모은 성과예요. 지금 이 샵에 연결된 광고 계정의 성과만 보여 드려요.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="기간">
        {PERIOD_PRESETS.map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? 'primary' : 'outline'}
            aria-pressed={days === d}
            onClick={() => navigate({ search: (prev) => ({ ...prev, days: d }), replace: true })}
            className="min-h-[44px] sm:min-h-0"
          >
            최근 {d}일
          </Button>
        ))}
        <span className="text-xs text-on-surface-variant tabular-nums">
          {range.date_from} ~ {range.date_to}
        </span>
      </div>

      <Tabs
        value={config.channel}
        onValueChange={(v) => navigate({ search: (prev) => ({ ...prev, channel: v }), replace: true })}
      >
        <TabsList>
          {CHANNELS.map((c) => (
            <TabsTrigger key={c.channel} value={c.channel} className="min-h-[44px] sm:min-h-0">
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <AdPerformanceReport key={config.channel} config={config} summary={summary} ads={ads} />
    </div>
  )
}
